/**
 * La forme d'un fil qui charge : des bulles de l'autre à gauche, celles de l'élève à droite.
 *
 * Il garde la géométrie du fil — la place de celui qui parle (`SPEAKER_AVATAR_SIZE`), les coins
 * et la hauteur d'une bulle d'une ligne — et se pose sous un `<DelayedSkeleton>` : c'est le fil
 * qui le monte, au premier chargement et en tête de liste pour les messages plus anciens.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Skeleton } from "../../atoms/Skeleton"
import { textLineBox } from "../../atoms/Skeleton/Skeleton"
import { MESSAGE_BUBBLE_PADDING_Y, MESSAGE_BUBBLE_TEXT_SIZE } from "../../molecules/MessageBubble"
import { MESSAGE_ROW_MAX_WIDTH, SPEAKER_AVATAR_SIZE } from "../../molecules/MessageRow"
import { spacing } from "../../theme/tokens/spacing"

/** Une ligne du texte d'une bulle, et une bulle d'une ligne : ses deux retraits et cette ligne. */
const LINE = textLineBox(MESSAGE_BUBBLE_TEXT_SIZE)
const ONE_LINE = 2 * spacing[MESSAGE_BUBBLE_PADDING_Y] + LINE

type Shape = { side: "other" | "own"; width: number; lines: number; avatar?: boolean }

/** Le cas courant : l'autre a écrit, l'élève a répondu, l'autre a repris. */
const FULL: Shape[] = [
    { side: "other", width: 220, lines: 1, avatar: true },
    { side: "other", width: 260, lines: 3 },
    { side: "own", width: 180, lines: 1 },
    { side: "other", width: 240, lines: 2, avatar: true },
    { side: "own", width: 140, lines: 1 },
]

/** Deux bulles, pour les messages plus anciens qui arrivent en tête de fil. */
const EARLIER: Shape[] = [
    { side: "other", width: 200, lines: 1, avatar: true },
    { side: "own", width: 160, lines: 1 },
]

export type ConversationThreadSkeletonProps = {
    /** `full` (défaut) : le fil entier · `earlier` : les messages plus anciens, en tête de fil. */
    variant?: "full" | "earlier"
    testID?: string
}

export function ConversationThreadSkeleton(props: ConversationThreadSkeletonProps) {
    const { variant = "full", testID } = props

    return (
        <View style={styles.root} testID={testID}>
            {(variant === "full" ? FULL : EARLIER).map((shape, index) => (
                <Row key={index} gap="2" align="end" justify={shape.side === "own" ? "end" : "start"}>
                    {shape.side === "other" && (
                        <View style={styles.gutter}>
                            {shape.avatar === true && <Skeleton shape="circle" size={SPEAKER_AVATAR_SIZE} />}
                        </View>
                    )}
                    <View style={styles.bubble}>
                        <Skeleton
                            shape="block"
                            width={shape.width}
                            height={ONE_LINE + (shape.lines - 1) * LINE}
                            radius="lg"
                        />
                    </View>
                </Row>
            ))}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { gap: theme.spacing["3"] },
    gutter: { width: SPEAKER_AVATAR_SIZE },
    bubble: { flexShrink: 1, maxWidth: MESSAGE_ROW_MAX_WIDTH },
}))
